import {
  addThread,
  addThreadReply,
  createMagicCommentProvider,
  createThreadRecord,
  deleteAnnotation,
  deleteThread,
  deleteThreadMessage,
  editThreadMessage,
  observePresence,
  observeThreads,
  readThreads,
  setThreadStatus,
} from "../collaboration";
import {
  type PresencePeer,
  decideFollowAction,
  dedupePeersById,
  derivePresencePeers,
  planFollowNavigation,
  presenceBarSide,
  resolveFollowedPeer,
} from "../collaboration/follow";
import { clearCursor, updateCursorPosition } from "../collaboration/presence";
import {
  createAnonymousUser,
  createUserFromName,
  loadUserIdentity,
  saveUserIdentity,
} from "../collaboration/user";
import type {
  AnnotationThread,
  ComponentMeta,
  DomTarget,
  MagicCommentController,
  MagicCommentOptions,
  MetadataProvider,
  UserInfo,
  UserPresence,
} from "../types";
import { MAGIC_COMMENT_VERSION } from "../version";
import {
  AGENT_FALLBACK_AVATAR,
  MC_AVATARS,
  avatarForName,
  randomMcAvatarUrl,
} from "./avatar";
import {
  commentPageLabel,
  parseReviewThreadsResponse,
  reviewThreadsEndpoint,
  sortThreadsByUpdatedDesc,
} from "./comments-panel";
import { defaultPageId } from "./page-id";
import {
  clearStoredThreads,
  loadStoredThreads,
  saveStoredThreads,
} from "./storage";
import {
  EMBED_MARKER_ATTRIBUTE,
  EMBED_ROOT_ATTRIBUTE,
  EMBED_STYLE_ID,
  embedStyles,
} from "./styles";

type UIRefs = {
  root: HTMLDivElement;
  markerLayer: HTMLDivElement;
  cursorLayer: HTMLDivElement;
  highlight: HTMLDivElement;
  namePrompt: HTMLDialogElement;
  nameCloseButton: HTMLButtonElement;
  nameInput: HTMLInputElement;
  nameAvatarButton: HTMLButtonElement;
  nameAvatarImage: HTMLImageElement;
  nameAvatarPopover: HTMLDivElement;
  nameSaveButton: HTMLButtonElement;
  nameSkipButton: HTMLButtonElement;
  presenceBar: HTMLDivElement;
  followIndicator: HTMLDivElement;
  toolbar: HTMLDivElement;
  toolbarToggleButton: HTMLButtonElement;
  annotateButton: HTMLButtonElement;
  commentsVisibilityButton: HTMLButtonElement;
  commentsFilterButton: HTMLButtonElement;
  commentsButton: HTMLButtonElement;
  clearButton: HTMLButtonElement;
  status: HTMLDivElement;
  commentsPanel: HTMLDivElement;
  commentsMeta: HTMLParagraphElement;
  commentsEmpty: HTMLParagraphElement;
  commentsList: HTMLDivElement;
  commentsScopePageButton: HTMLButtonElement;
  commentsScopeAllButton: HTMLButtonElement;
  popup: HTMLDialogElement;
  popupTitle: HTMLHeadingElement;
  infoButton: HTMLButtonElement;
  infoPopover: HTMLDivElement;
  popupCloseButton: HTMLButtonElement;
  statusBar: HTMLDivElement;
  statusOpenPill: HTMLButtonElement;
  statusManagedPill: HTMLButtonElement;
  statusResolvedPill: HTMLButtonElement;
  threadList: HTMLDivElement;
  replyContainer: HTMLDivElement;
  textarea: HTMLTextAreaElement;
  saveButton: HTMLButtonElement;
  cancelButton: HTMLButtonElement;
};

type PopupState =
  | {
      kind: "draft";
      target: DomTarget;
      x: number;
      y: number;
    }
  | {
      kind: "thread";
      threadId: string;
      x: number;
      y: number;
    }
  | null;

type ResolvedEmbedOptions = MagicCommentOptions & {
  reviewId: string;
  pageId: string;
  ui: NonNullable<MagicCommentOptions["ui"]>;
};

const DEFAULT_OPTIONS: Pick<
  MagicCommentOptions,
  "mode" | "promptForName" | "ui"
> = {
  mode: "dev",
  promptForName: true,
  ui: {
    enabled: true,
    badge: true,
    toolbar: true,
  },
};

const CLEAR_BUTTON_STORAGE_KEY = "magic_comment_enable_clear_button";
const FAB_CORNER_STORAGE_KEY = "magic_comment_embed_fab_corner";
const FAB_DRAG_THRESHOLD = 5;

/**
 * Follow state lives in sessionStorage (not localStorage) so it survives the
 * per-navigation unmount()+mount() remount but is naturally scoped to the tab
 * and cleared when the tab closes.
 */
const FOLLOW_STORAGE_KEY = "magic_comment_follow";

/** How long to wait for a soft SPA navigation to take effect before full load. */
const SOFT_NAV_TIMEOUT_MS = 700;

function loadFollowId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(FOLLOW_STORAGE_KEY);
  } catch {
    return null;
  }
}

function saveFollowId(id: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(FOLLOW_STORAGE_KEY, id);
  } catch {
    // ignore
  }
}

function clearFollowId(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(FOLLOW_STORAGE_KEY);
  } catch {
    // ignore
  }
}

type FabCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

function isFabCorner(value: unknown): value is FabCorner {
  return (
    value === "top-left" ||
    value === "top-right" ||
    value === "bottom-left" ||
    value === "bottom-right"
  );
}

function loadFabCorner(): FabCorner {
  if (typeof window === "undefined") return "bottom-right";
  try {
    const value = window.localStorage.getItem(FAB_CORNER_STORAGE_KEY);
    if (isFabCorner(value)) return value;
  } catch {
    // ignore
  }
  return "bottom-right";
}

function saveFabCorner(corner: FabCorner): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(FAB_CORNER_STORAGE_KEY, corner);
  } catch {
    // ignore
  }
}

function nearestCorner(centerX: number, centerY: number): FabCorner {
  if (typeof window === "undefined") return "bottom-right";
  const isLeft = centerX < window.innerWidth / 2;
  const isTop = centerY < window.innerHeight / 2;
  if (isTop && isLeft) return "top-left";
  if (isTop) return "top-right";
  if (isLeft) return "bottom-left";
  return "bottom-right";
}

function logWidgetBoot(): void {
  console.info(`[magic-comment] widget v${MAGIC_COMMENT_VERSION} mounted`);
}

function injectStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(EMBED_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = EMBED_STYLE_ID;
  style.textContent = embedStyles;
  document.head.appendChild(style);
}

function escapeSelector(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }

  return value.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
}

function normalizeOptions(input: MagicCommentOptions): ResolvedEmbedOptions {
  const reviewId =
    input.reviewId ?? (input.mode === "preview" ? "preview" : "dev");

  return {
    ...DEFAULT_OPTIONS,
    ...input,
    reviewId,
    pageId: input.pageId ?? defaultPageId(),
    collaboration: input.collaboration ?? undefined,
    metadataProvider: input.metadataProvider,
    ui: {
      ...DEFAULT_OPTIONS.ui,
      ...input.ui,
    },
  };
}

function isClearButtonEnabled(): boolean {
  if (typeof window === "undefined") return false;

  try {
    const value = window.localStorage.getItem(CLEAR_BUTTON_STORAGE_KEY);
    if (!value) return false;

    const normalized = value.trim().toLowerCase();
    return (
      normalized === "1" ||
      normalized === "true" ||
      normalized === "yes" ||
      normalized === "on"
    );
  } catch {
    return false;
  }
}

function extractTextSnippet(
  value: string | null | undefined,
): string | undefined {
  const text = value?.replace(/\s+/g, " ").trim();
  return text ? text.slice(0, 140) : undefined;
}

function buildSelectorSegment(element: HTMLElement): string {
  const tag = element.tagName.toLowerCase();
  if (element.id) {
    return `${tag}#${escapeSelector(element.id)}`;
  }

  const stableClasses = [...element.classList]
    .filter(
      (value) =>
        !/^(hover|focus|active|selected|open|closed|enter|leave)/.test(value),
    )
    .slice(0, 2);
  const classSelector =
    stableClasses.length > 0
      ? stableClasses.map((value) => `.${escapeSelector(value)}`).join("")
      : "";

  const parent = element.parentElement;
  if (!parent) return `${tag}${classSelector}`;

  const siblings = [...parent.children].filter(
    (candidate) => candidate.tagName === element.tagName,
  );
  if (siblings.length <= 1) return `${tag}${classSelector}`;

  const index = siblings.indexOf(element) + 1;
  return `${tag}${classSelector}:nth-of-type(${index})`;
}

function buildSelectorPath(element: HTMLElement): string[] {
  const path: string[] = [];
  let current: HTMLElement | null = element;
  let depth = 0;

  while (current && current !== document.body && depth < 6) {
    path.unshift(buildSelectorSegment(current));
    current = current.parentElement;
    depth += 1;
  }

  return path;
}

function buildComponentMeta(
  element: HTMLElement,
  provider?: MetadataProvider,
): ComponentMeta | undefined {
  if (!provider?.isAvailable()) return undefined;

  try {
    return provider.getComponentMeta(element) ?? undefined;
  } catch {
    return undefined;
  }
}

function buildDomTarget(
  element: HTMLElement,
  event: MouseEvent,
  provider?: MetadataProvider,
): DomTarget {
  const rect = element.getBoundingClientRect();
  const textQuote = extractTextSnippet(
    element.innerText || element.textContent,
  );
  const attributes: Record<string, string> = {};
  const selectedAttributes = ["data-testid", "aria-label", "name", "role"];
  selectedAttributes.forEach((attribute) => {
    const value = element.getAttribute(attribute);
    if (value) attributes[attribute] = value;
  });

  return {
    kind: "dom",
    url: window.location.href,
    anchor: {
      selectorPath: buildSelectorPath(element),
      tagName: element.tagName.toLowerCase(),
      textQuote,
      textContextBefore: extractTextSnippet(
        element.previousElementSibling?.textContent,
      ),
      textContextAfter: extractTextSnippet(
        element.nextElementSibling?.textContent,
      ),
      classList: [...element.classList],
      attributes: Object.keys(attributes).length > 0 ? attributes : undefined,
    },
    snapshot: {
      pageX: event.pageX,
      pageY: event.pageY,
      viewportX: event.clientX,
      viewportY: event.clientY,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      elementRect: {
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    },
    componentMeta: buildComponentMeta(element, provider),
  };
}

function resolveDomTarget(target: DomTarget): HTMLElement | null {
  const exactSelector = target.anchor.selectorPath.join(" > ");
  if (exactSelector) {
    try {
      const direct = document.querySelector(exactSelector);
      if (direct instanceof HTMLElement) return direct;
    } catch {
      // Invalid selector (e.g. from a remote peer); fall back to heuristics.
    }
  }

  let candidates: HTMLElement[];
  try {
    candidates = [
      ...document.querySelectorAll<HTMLElement>(target.anchor.tagName),
    ];
  } catch {
    return null;
  }
  const classList = target.anchor.classList ?? [];

  const matchingClasses = classList.length
    ? candidates.filter((candidate) =>
        classList.every((name) => candidate.classList.contains(name)),
      )
    : candidates;

  const preferred = target.anchor.textQuote
    ? matchingClasses.find((candidate) =>
        (candidate.innerText || candidate.textContent || "")
          .replace(/\s+/g, " ")
          .includes(target.anchor.textQuote!),
      )
    : matchingClasses[0];

  return preferred ?? null;
}

function getThreadViewportPosition(
  thread: AnnotationThread,
): { left: number; top: number } | null {
  if (thread.target.kind !== "dom") return null;

  const element = resolveDomTarget(thread.target);
  if (element) {
    const rect = element.getBoundingClientRect();
    return {
      left: rect.left + rect.width / 2 - 14,
      top: Math.max(8, rect.top - 14),
    };
  }

  return {
    left: thread.target.snapshot.pageX - window.scrollX - 14,
    top: thread.target.snapshot.pageY - window.scrollY - 14,
  };
}

/**
 * Per-thread guard: one bad thread (malformed anchor, hostile remote data)
 * must never prevent the other threads from rendering.
 */
function safeThreadViewportPosition(
  thread: AnnotationThread,
): { left: number; top: number } | null {
  try {
    return getThreadViewportPosition(thread);
  } catch {
    return null;
  }
}

const LOCATION_CHANGE_EVENT = "magic-comment:locationchange";

type PatchedWindow = Window & { __MAGIC_COMMENT_HISTORY_PATCHED__?: boolean };

/**
 * Patch history.pushState/replaceState once (globally) so SPA route changes
 * dispatch a custom event the controller can listen to. The patch is
 * intentionally never restored: it is idempotent, transparent to the host,
 * and restoring it safely across multiple mounts is riskier than keeping it.
 */
function ensureHistoryPatched(): void {
  if (typeof window === "undefined" || typeof history === "undefined") return;
  const patched = window as PatchedWindow;
  if (patched.__MAGIC_COMMENT_HISTORY_PATCHED__) return;
  patched.__MAGIC_COMMENT_HISTORY_PATCHED__ = true;

  const patch = (method: "pushState" | "replaceState"): void => {
    const original = history[method].bind(history);
    history[method] = (
      ...args: Parameters<History["pushState"]>
    ): ReturnType<History["pushState"]> => {
      const result = original(...args);
      try {
        window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
      } catch {
        // Never let widget listeners break host navigation.
      }
      return result;
    };
  };

  patch("pushState");
  patch("replaceState");
}

function getMarkerSpreadPosition(
  base: { left: number; top: number },
  index: number,
  count: number,
): { left: number; top: number } {
  if (count <= 1) return base;

  const radius = count === 2 ? 18 : count <= 4 ? 22 : 28;
  const angle = -Math.PI / 2 + (index * (Math.PI * 2)) / count;

  return {
    left: base.left + Math.cos(angle) * radius,
    top: base.top + Math.sin(angle) * radius,
  };
}

function getAnnotatableElement(
  target: EventTarget | null,
  root: HTMLElement,
): HTMLElement | null {
  if (!(target instanceof HTMLElement)) return null;
  if (root.contains(target)) return null;
  if (target === document.body || target === document.documentElement)
    return null;
  return target;
}

function describeThread(thread: AnnotationThread): string {
  if (thread.target.kind !== "dom") return thread.target.frameId;

  const parts = [thread.target.anchor.selectorPath.join(" > ")];
  if (thread.target.componentMeta?.framework) {
    parts.push(`framework: ${thread.target.componentMeta.framework}`);
  }
  if (thread.target.componentMeta?.componentPath) {
    parts.push(`component: ${thread.target.componentMeta.componentPath}`);
  }
  if (thread.target.componentMeta?.sourceFile) {
    parts.push(`source: ${thread.target.componentMeta.sourceFile}`);
  }
  return parts.filter(Boolean).join(" • ");
}

function summarizeThread(thread: AnnotationThread): string {
  const firstMessage = thread.messages[0]?.text?.trim();
  if (firstMessage) return firstMessage;

  if (thread.target.kind === "dom") {
    return thread.target.anchor.textQuote || thread.target.url;
  }

  return thread.target.frameId;
}

function getThreadStatusLabel(status: AnnotationThread["status"]): string {
  switch (status) {
    case "open":
      return "new";
    case "managed":
      return "discussed";
    case "resolved":
      return "resolved";
  }
}

/** Validate that a string parses as an https: URL (guards against js:/data: URLs). */
function toHttpsUrl(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? value : null;
  } catch {
    return null;
  }
}

/** Validate that a string parses as an http(s): URL (guards against js:/data: URLs). */
function toHttpUrl(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? value : null;
  } catch {
    return null;
  }
}

/**
 * Build a circular <img> avatar for the author (never innerHTML — the src is
 * only set after validating the protocol). Agents keep their explicit https
 * avatar logo; every other author (humans) gets a deterministic hosted avatar
 * derived from their name. On a load error the image falls back to the provided
 * initial dot. Always returns an <img>.
 */
function createAuthorAvatar(
  author: UserInfo,
  fallbackDot: HTMLElement,
): HTMLImageElement {
  const src = toHttpsUrl(author.avatarUrl) ?? avatarForName(author.name);

  const img = document.createElement("img");
  img.className = "magic-comment-embed-thread-avatar";
  img.alt = author.name;
  if (src === AGENT_FALLBACK_AVATAR) {
    img.style.objectFit = "contain";
  }
  img.addEventListener("error", () => {
    img.replaceWith(fallbackDot);
  });
  img.src = src;
  return img;
}

function createUI(): UIRefs {
  const root = document.createElement("div");
  root.setAttribute(EMBED_ROOT_ATTRIBUTE, "");

  const markerLayer = document.createElement("div");
  markerLayer.className = "magic-comment-embed-layer";

  const cursorLayer = document.createElement("div");
  cursorLayer.className = "magic-comment-embed-layer";

  const highlight = document.createElement("div");
  highlight.className = "magic-comment-embed-highlight";
  highlight.hidden = true;

  const namePrompt = document.createElement("dialog");
  namePrompt.className = "magic-comment-embed-name-prompt";
  // Escape must not dismiss the identity dialog (it stays open on overlay
  // click too); closing is driven explicitly via the close/skip buttons.
  namePrompt.addEventListener("cancel", (event) => event.preventDefault());

  const nameCard = document.createElement("div");
  nameCard.className = "magic-comment-embed-name-card";

  const nameHeader = document.createElement("div");
  nameHeader.className = "magic-comment-embed-name-header";

  const nameBrand = document.createElement("div");
  nameBrand.className = "magic-comment-embed-name-brand";

  const nameBrandLogo = document.createElement("img");
  nameBrandLogo.className = "magic-comment-embed-name-brand-logo";
  nameBrandLogo.src = "https://app.magic-comment.com/magic-logo.png";
  nameBrandLogo.alt = "";

  const nameBrandText = document.createElement("span");
  nameBrandText.textContent = "Magic Comment";

  nameBrand.append(nameBrandLogo, nameBrandText);

  const nameCloseButton = document.createElement("button");
  nameCloseButton.className = "magic-comment-embed-icon-button";
  nameCloseButton.type = "button";
  nameCloseButton.setAttribute("aria-label", "Close");
  nameCloseButton.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;

  nameHeader.append(nameBrand, nameCloseButton);

  const nameTitle = document.createElement("h2");
  nameTitle.textContent = "Who is commenting?";

  const nameInputWrap = document.createElement("div");
  nameInputWrap.className = "magic-comment-embed-name-input-wrap";

  const nameInput = document.createElement("input");
  nameInput.className = "magic-comment-embed-input";
  nameInput.type = "text";
  nameInput.placeholder = "Your name";
  nameInput.autocomplete = "name";
  nameInput.maxLength = 80;

  const nameAvatarButton = document.createElement("button");
  nameAvatarButton.className = "magic-comment-embed-name-avatar-button";
  nameAvatarButton.type = "button";
  nameAvatarButton.setAttribute("aria-label", "Choose avatar");

  const nameAvatarImage = document.createElement("img");
  nameAvatarImage.className = "magic-comment-embed-name-avatar-image";
  nameAvatarImage.alt = "";

  const nameAvatarChevron = document.createElement("span");
  nameAvatarChevron.className = "magic-comment-embed-name-avatar-chevron";
  nameAvatarChevron.textContent = "⌄";

  nameAvatarButton.append(nameAvatarImage, nameAvatarChevron);

  const nameAvatarPopover = document.createElement("div");
  nameAvatarPopover.className = "magic-comment-embed-name-avatar-popover";
  nameAvatarPopover.hidden = true;
  MC_AVATARS.forEach((avatar) => {
    const option = document.createElement("button");
    option.className = "magic-comment-embed-name-avatar-option";
    option.type = "button";
    option.dataset.avatarUrl = avatar.url;
    option.setAttribute("aria-label", avatar.label);

    const img = document.createElement("img");
    img.src = avatar.url;
    img.alt = "";
    option.appendChild(img);
    nameAvatarPopover.appendChild(option);
  });

  nameInputWrap.append(nameInput, nameAvatarButton, nameAvatarPopover);

  const nameActions = document.createElement("div");
  nameActions.className = "magic-comment-embed-popup-actions";

  const nameActionsSpacer = document.createElement("div");
  const nameActionsRight = document.createElement("div");
  nameActionsRight.className = "magic-comment-embed-popup-actions-right";

  const nameSkipButton = document.createElement("button");
  nameSkipButton.className = "magic-comment-embed-popup-button";
  nameSkipButton.type = "button";
  nameSkipButton.textContent = "Cancel";

  const nameSaveButton = document.createElement("button");
  nameSaveButton.className = "magic-comment-embed-popup-button";
  nameSaveButton.type = "button";
  nameSaveButton.dataset.primary = "true";
  nameSaveButton.textContent = "Continue";

  nameActionsRight.append(nameSkipButton, nameSaveButton);
  nameActions.append(nameActionsSpacer, nameActionsRight);
  nameCard.append(nameHeader, nameTitle, nameInputWrap, nameActions);
  namePrompt.appendChild(nameCard);

  const presenceBar = document.createElement("div");
  presenceBar.className = "magic-comment-embed-presence-bar";
  presenceBar.hidden = true;

  const followIndicator = document.createElement("div");
  followIndicator.className = "magic-comment-embed-follow-indicator";
  followIndicator.hidden = true;

  const toolbar = document.createElement("div");
  toolbar.className = "magic-comment-embed-toolbar";

  const toolbarToggleButton = document.createElement("button");
  toolbarToggleButton.className = "magic-comment-embed-toolbar-toggle";
  toolbarToggleButton.type = "button";
  toolbarToggleButton.setAttribute("aria-label", "Toggle comment actions");
  toolbarToggleButton.dataset.expanded = "false";
  toolbarToggleButton.innerHTML = `
    <svg class="magic-comment-embed-toolbar-toggle-icon" data-icon="open" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
    <svg class="magic-comment-embed-toolbar-toggle-icon" data-icon="close" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  `;

  const annotateButton = document.createElement("button");
  annotateButton.className = "magic-comment-embed-label-toolbar-button";
  annotateButton.type = "button";
  annotateButton.setAttribute("aria-label", "Annotate");
  annotateButton.title = "Annotate";
  annotateButton.innerHTML = `<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg><span>Annotate</span>`;

  const commentsButton = document.createElement("button");
  commentsButton.className = "magic-comment-embed-icon-toolbar-button";
  commentsButton.type = "button";
  commentsButton.setAttribute("aria-label", "Comments");
  commentsButton.title = "Comments";
  commentsButton.innerHTML = `<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`;

  toolbar.append(annotateButton, commentsButton);

  // View-settings controls relocated into the comments popover (see below).
  const commentsVisibilityButton = document.createElement("button");
  commentsVisibilityButton.className = "magic-comment-embed-toggle";
  commentsVisibilityButton.type = "button";
  commentsVisibilityButton.textContent = "Hide comments";
  commentsVisibilityButton.dataset.active = "true";

  const commentsFilterButton = document.createElement("button");
  commentsFilterButton.className = "magic-comment-embed-toggle";
  commentsFilterButton.type = "button";
  commentsFilterButton.textContent = "Hide discussed";

  const clearButton = document.createElement("button");
  clearButton.className = "magic-comment-embed-clear";
  clearButton.type = "button";
  clearButton.textContent = "Clear all";

  const status = document.createElement("div");
  status.className = "magic-comment-embed-status";
  status.hidden = true;

  const commentsPanel = document.createElement("div");
  commentsPanel.className = "magic-comment-embed-comments";
  commentsPanel.hidden = true;

  const commentsHeader = document.createElement("div");
  commentsHeader.className = "magic-comment-embed-comments-header";

  const commentsHeading = document.createElement("div");
  const commentsTitle = document.createElement("h3");
  commentsTitle.textContent = "Comments";
  const commentsMeta = document.createElement("p");
  commentsMeta.className = "magic-comment-embed-meta";
  commentsMeta.textContent = "0 open • 0 total";
  commentsHeading.append(commentsTitle, commentsMeta);

  const commentsScopeControl = document.createElement("div");
  commentsScopeControl.className = "magic-comment-embed-scope";

  const commentsScopePageButton = document.createElement("button");
  commentsScopePageButton.className = "magic-comment-embed-scope-button";
  commentsScopePageButton.type = "button";
  commentsScopePageButton.textContent = "This page";
  commentsScopePageButton.dataset.active = "true";

  const commentsScopeAllButton = document.createElement("button");
  commentsScopeAllButton.className = "magic-comment-embed-scope-button";
  commentsScopeAllButton.type = "button";
  commentsScopeAllButton.textContent = "All";
  commentsScopeAllButton.dataset.active = "false";

  commentsScopeControl.append(commentsScopePageButton, commentsScopeAllButton);
  commentsHeader.append(commentsHeading, commentsScopeControl);

  const commentsEmpty = document.createElement("p");
  commentsEmpty.className = "magic-comment-embed-comments-empty";
  commentsEmpty.textContent = "No comments for this page yet.";

  const commentsList = document.createElement("div");
  commentsList.className = "magic-comment-embed-comments-list";

  const commentsSettings = document.createElement("div");
  commentsSettings.className = "magic-comment-embed-comments-settings";

  const commentsSettingsToggles = document.createElement("div");
  commentsSettingsToggles.className = "magic-comment-embed-settings-toggles";
  commentsSettingsToggles.append(
    commentsVisibilityButton,
    commentsFilterButton,
  );

  commentsSettings.append(commentsSettingsToggles, clearButton);

  commentsPanel.append(
    commentsHeader,
    commentsEmpty,
    commentsList,
    commentsSettings,
  );

  const popup = document.createElement("dialog");
  popup.className = "magic-comment-embed-popup";
  // Closing is driven by our own Cancel/Close buttons; keep Escape from
  // auto-closing the native dialog so behaviour matches the previous popup.
  popup.addEventListener("cancel", (event) => event.preventDefault());

  const popupHeader = document.createElement("div");
  popupHeader.className = "magic-comment-embed-popup-header";

  const popupTitle = document.createElement("h3");

  const popupHeaderActions = document.createElement("div");
  popupHeaderActions.className = "magic-comment-embed-popup-header-actions";

  const infoWrapper = document.createElement("div");
  infoWrapper.className = "magic-comment-embed-info-wrapper";

  const infoButton = document.createElement("button");
  infoButton.className = "magic-comment-embed-icon-button";
  infoButton.type = "button";
  infoButton.setAttribute("aria-label", "Show element info");
  infoButton.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>`;

  const infoPopover = document.createElement("div");
  infoPopover.className = "magic-comment-embed-info-popover";
  infoPopover.hidden = true;

  infoWrapper.append(infoButton, infoPopover);

  const popupCloseButton = document.createElement("button");
  popupCloseButton.className = "magic-comment-embed-icon-button";
  popupCloseButton.type = "button";
  popupCloseButton.setAttribute("aria-label", "Close");
  popupCloseButton.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;

  popupHeaderActions.append(infoWrapper, popupCloseButton);
  popupHeader.append(popupTitle, popupHeaderActions);

  const statusBar = document.createElement("div");
  statusBar.className = "magic-comment-embed-status-bar";
  statusBar.hidden = true;

  const createStatusPill = (
    status: "open" | "managed" | "resolved",
    label: string,
  ): HTMLButtonElement => {
    const pill = document.createElement("button");
    pill.className = "magic-comment-embed-status-pill";
    pill.type = "button";
    pill.dataset.status = status;
    pill.textContent = label;
    return pill;
  };

  const statusOpenPill = createStatusPill("open", "Open");
  const statusManagedPill = createStatusPill("managed", "Discussed");
  const statusResolvedPill = createStatusPill("resolved", "Resolved");

  statusBar.append(statusOpenPill, statusManagedPill, statusResolvedPill);

  const threadList = document.createElement("div");
  threadList.className = "magic-comment-embed-thread";

  const replyContainer = document.createElement("div");
  replyContainer.className = "magic-comment-embed-reply";

  const textarea = document.createElement("textarea");
  textarea.className = "magic-comment-embed-textarea";
  textarea.placeholder = "Describe the change you want to make.";

  const replyActions = document.createElement("div");
  replyActions.className = "magic-comment-embed-reply-actions";

  const cancelButton = document.createElement("button");
  cancelButton.className = "magic-comment-embed-popup-button";
  cancelButton.dataset.size = "sm";
  cancelButton.dataset.variant = "ghost";
  cancelButton.type = "button";
  cancelButton.textContent = "Cancel";

  const saveButton = document.createElement("button");
  saveButton.className = "magic-comment-embed-popup-button";
  saveButton.dataset.size = "sm";
  saveButton.dataset.primary = "true";
  saveButton.type = "button";
  saveButton.textContent = "Save";

  replyActions.append(cancelButton, saveButton);
  replyContainer.append(textarea, replyActions);

  popup.append(popupHeader, statusBar, threadList, replyContainer);
  root.append(
    highlight,
    markerLayer,
    cursorLayer,
    namePrompt,
    presenceBar,
    followIndicator,
    toolbar,
    toolbarToggleButton,
    status,
    commentsPanel,
    popup,
  );
  document.body.appendChild(root);

  return {
    root,
    markerLayer,
    cursorLayer,
    highlight,
    namePrompt,
    nameCloseButton,
    nameInput,
    nameAvatarButton,
    nameAvatarImage,
    nameAvatarPopover,
    nameSaveButton,
    nameSkipButton,
    presenceBar,
    followIndicator,
    toolbar,
    toolbarToggleButton,
    annotateButton,
    commentsVisibilityButton,
    commentsFilterButton,
    commentsButton,
    clearButton,
    status,
    commentsPanel,
    commentsMeta,
    commentsEmpty,
    commentsList,
    commentsScopePageButton,
    commentsScopeAllButton,
    popup,
    popupTitle,
    infoButton,
    infoPopover,
    popupCloseButton,
    statusBar,
    statusOpenPill,
    statusManagedPill,
    statusResolvedPill,
    threadList,
    replyContainer,
    textarea,
    saveButton,
    cancelButton,
  };
}

export function mountMagicComment(
  input: MagicCommentOptions,
): MagicCommentController {
  let derivePageIdFromLocation = input.pageId == null;
  let options = normalizeOptions(input);
  let mounted = false;
  let pinMode = false;
  let popupState: PopupState = null;
  let hoveredElement: HTMLElement | null = null;
  const storedUser = loadUserIdentity();
  let hasUserIdentity = Boolean(storedUser);
  let currentUser: UserInfo =
    storedUser ?? createAnonymousUser(randomMcAvatarUrl());
  let promptingForName = false;
  let nameDialogMode: "onboarding" | "edit" | null = null;
  let nameDraft = "";
  let nameDraftAvatarUrl = currentUser.avatarUrl ?? randomMcAvatarUrl();
  let nameAvatarPopoverOpen = false;
  let ui: UIRefs | null = null;
  let provider: ReturnType<typeof createMagicCommentProvider> | null = null;
  let presenceProvider: ReturnType<typeof createMagicCommentProvider> | null =
    null;
  let unsubscribeThreads: (() => void) | null = null;
  let unsubscribePresence: (() => void) | null = null;
  let unsubscribePresenceBar: (() => void) | null = null;
  // Stable-id of the teammate we are following, or null. Persisted in
  // sessionStorage so it survives the remount on every page navigation.
  let followId: string | null = loadFollowId();
  let followObserver: MutationObserver | null = null;
  let followNavTimer: number | undefined;
  let followNavInFlight = false;
  let threads: AnnotationThread[] = [];
  let remotePresences: UserPresence[] = [];
  let statusTimer: number | undefined;
  let commentsOpen = false;
  let commentsScope: "page" | "all" = "page";
  let allThreads: AnnotationThread[] = [];
  let allThreadsLoading = false;
  let allThreadsError: string | null = null;
  // Monotonic token so a slow/failed fetch never overwrites a newer one.
  let allThreadsRequestId = 0;
  let commentsVisible = true;
  let hideDiscussedComments = false;
  let toolbarExpanded = false;
  let editingMessageId: string | null = null;
  let editingDraft = "";
  let editJustStarted = false;
  let confirmingDeleteId: string | null = null;
  let infoPopoverOpen = false;
  let fabCorner: FabCorner = loadFabCorner();
  let fabDrag: {
    pointerId: number;
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
    moved: boolean;
  } | null = null;
  let providerConnected = false;
  let scrollRafHandle: number | null = null;
  let cursorUpdateTimer: number | undefined;
  let pendingCursor: { x: number; y: number } | null = null;

  const localThreadState = () =>
    loadStoredThreads(options.projectId, options.reviewId, options.pageId);

  function setThreads(nextThreads: AnnotationThread[]): void {
    threads = nextThreads;

    // Always keep a local backup copy, even when a collab provider exists, so
    // comments are not lost when the server is unreachable. Only skip the
    // pre-connection empty state of a provider doc, which would wipe a
    // previously saved backup before the first sync.
    if (!provider || providerConnected || nextThreads.length > 0) {
      saveStoredThreads(
        options.projectId,
        options.reviewId,
        options.pageId,
        nextThreads,
      );
    }

    renderMarkers();
    refreshPopup();
    refreshCommentsPanel();
  }

  function showStatus(message: string): void {
    if (!ui) return;
    ui.status.classList.remove("magic-comment-embed-status-connected");
    ui.status.textContent = message;
    ui.status.hidden = false;
    window.clearTimeout(statusTimer);
    statusTimer = window.setTimeout(() => {
      if (ui) ui.status.hidden = true;
    }, 2400);
  }

  function showConnectedStatus(): void {
    if (!ui) return;
    ui.status.classList.add("magic-comment-embed-status-connected");
    ui.status.textContent = "";

    const logo = document.createElement("img");
    logo.className = "magic-comment-embed-status-logo";
    logo.src = "https://app.magic-comment.com/magic-logo.png";
    logo.alt = "";

    const label = document.createElement("span");
    label.textContent = "Connected";

    ui.status.append(logo, label);
    ui.status.hidden = false;
    window.clearTimeout(statusTimer);
    statusTimer = window.setTimeout(() => {
      if (ui) ui.status.hidden = true;
    }, 2400);
  }

  function syncToolbarVisibility(): void {
    if (!ui) return;
    const hideToolbarUI = options.ui.toolbar === false || promptingForName;
    ui.toolbar.hidden = hideToolbarUI;
    ui.toolbarToggleButton.hidden = hideToolbarUI;
    // "Clear all" lives in the comments popover but stays gated behind the same
    // opt-in flag it had in the toolbar.
    ui.clearButton.hidden = !isClearButtonEnabled();
    ui.commentsButton.dataset.active = commentsOpen ? "true" : "false";
    ui.toolbar.dataset.expanded = toolbarExpanded ? "true" : "false";
    // The FAB is the single close affordance: it shows its X state (and the
    // click closes) whenever the toolbar is expanded OR the comments panel is
    // open, so there is no separate in-panel Close button.
    ui.toolbarToggleButton.dataset.expanded =
      toolbarExpanded || commentsOpen ? "true" : "false";
  }

  function syncCorner(): void {
    if (!ui) return;
    ui.toolbarToggleButton.dataset.corner = fabCorner;
    ui.toolbar.dataset.corner = fabCorner;
    ui.status.dataset.corner = fabCorner;
    ui.commentsPanel.dataset.corner = fabCorner;
    // The presence bar sits top-right and would overlap the toolbar when the
    // FAB is docked there — flip it to the left in that case.
    ui.presenceBar.dataset.side = presenceBarSide(fabCorner);
  }

  function clearFabInlinePosition(): void {
    if (!ui) return;
    const button = ui.toolbarToggleButton;
    button.style.left = "";
    button.style.top = "";
    button.style.right = "";
    button.style.bottom = "";
    delete button.dataset.dragging;
  }

  function setFabCorner(next: FabCorner): void {
    fabCorner = next;
    saveFabCorner(next);
    clearFabInlinePosition();
    syncCorner();
  }

  function setToolbarExpanded(next: boolean): void {
    toolbarExpanded = next;
    syncToolbarVisibility();
    renderPresenceBar();
  }

  function collapseToolbar(): void {
    setToolbarExpanded(false);
  }

  function syncCommentsVisibilityButton(): void {
    if (!ui) return;
    ui.commentsVisibilityButton.textContent = commentsVisible
      ? "Hide comments"
      : "Show comments";
    ui.commentsVisibilityButton.dataset.active = commentsVisible
      ? "true"
      : "false";
    ui.commentsVisibilityButton.setAttribute(
      "aria-pressed",
      commentsVisible ? "true" : "false",
    );
  }

  function syncCommentsFilterButton(): void {
    if (!ui) return;
    ui.commentsFilterButton.textContent = hideDiscussedComments
      ? "Show all"
      : "Hide discussed";
    ui.commentsFilterButton.dataset.active = hideDiscussedComments
      ? "true"
      : "false";
    ui.commentsFilterButton.setAttribute(
      "aria-pressed",
      hideDiscussedComments ? "true" : "false",
    );
  }

  function setCommentsVisible(nextVisible: boolean): void {
    commentsVisible = nextVisible;

    // The visibility toggle now lives inside the comments popover, so hiding
    // comments must not close the panel (that would hide the control itself);
    // only dismiss any open thread popup.
    if (!commentsVisible) {
      closePopup();
    }

    syncCommentsVisibilityButton();
    renderMarkers();
    refreshCommentsPanel();
  }

  function setHideDiscussedComments(nextValue: boolean): void {
    hideDiscussedComments = nextValue;
    syncCommentsFilterButton();
    renderMarkers();
    refreshCommentsPanel();
  }

  function closeCommentsPanel(): void {
    commentsOpen = false;
    renderPresenceBar();
    syncToolbarVisibility();
    if (!ui) return;
    ui.commentsPanel.hidden = true;
  }

  function syncCommentsScopeButtons(): void {
    if (!ui) return;
    ui.commentsScopePageButton.dataset.active =
      commentsScope === "page" ? "true" : "false";
    ui.commentsScopeAllButton.dataset.active =
      commentsScope === "all" ? "true" : "false";
  }

  /**
   * Fetch every thread across all pages of the review over HTTP. Guarded by a
   * monotonic request id (races), a 10s timeout, and a broad catch so a failed
   * request only shows an inline error and never crashes the host page.
   */
  function fetchAllThreads(): void {
    const collab = options.collaboration;
    const endpoint = reviewThreadsEndpoint(
      collab?.serverUrl,
      options.projectId,
      options.reviewId,
    );
    if (!collab || !endpoint) {
      allThreads = [];
      allThreadsLoading = false;
      allThreadsError = "All-pages comments need a collaboration server.";
      refreshCommentsPanel();
      return;
    }

    const requestId = ++allThreadsRequestId;
    allThreadsLoading = true;
    allThreadsError = null;
    refreshCommentsPanel();

    const headers: Record<string, string> = {};
    if (collab.apiKey) headers.Authorization = `Bearer ${collab.apiKey}`;

    fetch(endpoint, {
      method: "GET",
      headers,
      signal: AbortSignal.timeout(10000),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (requestId !== allThreadsRequestId) return;
        allThreads = sortThreadsByUpdatedDesc(parseReviewThreadsResponse(data));
        allThreadsLoading = false;
        allThreadsError = null;
        refreshCommentsPanel();
      })
      .catch(() => {
        if (requestId !== allThreadsRequestId) return;
        allThreads = [];
        allThreadsLoading = false;
        allThreadsError = "Could not load all comments. Please try again.";
        refreshCommentsPanel();
      });
  }

  function setCommentsScope(next: "page" | "all"): void {
    if (commentsScope === next) return;
    commentsScope = next;
    syncCommentsScopeButtons();
    if (next === "all") {
      // Always refetch on entry so the list reflects the latest server state.
      fetchAllThreads();
    } else {
      refreshCommentsPanel();
    }
  }

  function openThreadPopup(thread: AnnotationThread): void {
    const position = safeThreadViewportPosition(thread);
    closeCommentsPanel();
    openPopup({
      kind: "thread",
      threadId: thread.id,
      x: position ? position.left + 36 : Math.max(24, window.innerWidth - 408),
      y: position ? position.top : 96,
    });
  }

  function refreshCommentsPanel(): void {
    if (!ui) return;

    const nextUi = ui;

    nextUi.commentsPanel.hidden = !commentsOpen;
    if (!commentsOpen) return;

    syncCommentsScopeButtons();

    if (commentsScope === "all") {
      refreshAllCommentsPanel(nextUi);
      return;
    }

    const pageThreads = threads.filter(
      (thread) => thread.pageId === options.pageId,
    );
    const openCount = pageThreads.filter(
      (thread) => thread.status === "open",
    ).length;
    const managedCount = pageThreads.filter(
      (thread) => thread.status === "managed",
    ).length;
    const visibleThreads = hideDiscussedComments
      ? pageThreads.filter((thread) => thread.status === "open")
      : pageThreads;

    nextUi.commentsMeta.textContent = `${openCount} new • ${managedCount} discussed • ${pageThreads.length} total`;
    nextUi.commentsList.innerHTML = "";
    nextUi.commentsEmpty.textContent = "No comments for this page yet.";
    nextUi.commentsEmpty.hidden = visibleThreads.length > 0;

    visibleThreads.forEach((thread) => {
      const stableIndex = pageThreads.findIndex(
        (entry) => entry.id === thread.id,
      );

      const item = document.createElement("button");
      item.className = "magic-comment-embed-comments-item";
      item.type = "button";
      item.dataset.status = thread.status;

      const header = document.createElement("div");
      header.className = "magic-comment-embed-comments-item-header";

      const number = document.createElement("span");
      number.className = "magic-comment-embed-comments-item-number";
      number.textContent = String(stableIndex + 1);

      const author = document.createElement("span");
      author.className = "magic-comment-embed-comments-item-author";
      author.textContent =
        thread.messages[0]?.author.name ?? thread.author.name;

      const status = document.createElement("span");
      status.className = "magic-comment-embed-comments-item-status";
      status.textContent = getThreadStatusLabel(thread.status);

      const text = document.createElement("p");
      text.className = "magic-comment-embed-comments-item-text";
      text.textContent = summarizeThread(thread);

      header.append(number, author, status);
      item.append(header, text);
      item.addEventListener("click", () => {
        openThreadPopup(thread);
      });

      nextUi.commentsList.appendChild(item);
    });
  }

  /**
   * Render the "All" scope: every comment across the whole review (fetched over
   * HTTP). Off-page items show their page path and navigate there on click;
   * on-page items open the thread popup as usual.
   */
  function refreshAllCommentsPanel(nextUi: UIRefs): void {
    nextUi.commentsList.innerHTML = "";

    if (allThreadsLoading) {
      nextUi.commentsMeta.textContent = "Loading all comments…";
      nextUi.commentsEmpty.hidden = true;
      return;
    }

    if (allThreadsError) {
      nextUi.commentsMeta.textContent = "";
      nextUi.commentsEmpty.textContent = allThreadsError;
      nextUi.commentsEmpty.hidden = false;
      return;
    }

    nextUi.commentsMeta.textContent = `${allThreads.length} total across all pages`;
    nextUi.commentsEmpty.textContent = "No comments in this review yet.";
    nextUi.commentsEmpty.hidden = allThreads.length > 0;

    allThreads.forEach((thread, index) => {
      const isThisPage =
        thread.pageId !== undefined && thread.pageId === options.pageId;

      const item = document.createElement("button");
      item.className = "magic-comment-embed-comments-item";
      item.type = "button";
      item.dataset.status = thread.status;

      const header = document.createElement("div");
      header.className = "magic-comment-embed-comments-item-header";

      const number = document.createElement("span");
      number.className = "magic-comment-embed-comments-item-number";
      number.textContent = String(index + 1);

      const author = document.createElement("span");
      author.className = "magic-comment-embed-comments-item-author";
      author.textContent =
        thread.messages[0]?.author.name ?? thread.author.name;

      const status = document.createElement("span");
      status.className = "magic-comment-embed-comments-item-status";
      status.textContent = getThreadStatusLabel(thread.status);

      const text = document.createElement("p");
      text.className = "magic-comment-embed-comments-item-text";
      text.textContent = summarizeThread(thread);

      const page = document.createElement("span");
      page.className = "magic-comment-embed-comments-item-page";
      page.dataset.here = isThisPage ? "true" : "false";
      page.textContent = commentPageLabel(thread, options.pageId);

      header.append(number, author, status);
      item.append(header, text, page);
      item.addEventListener("click", () => {
        if (isThisPage) {
          openThreadPopup(thread);
          return;
        }
        // Off-page: navigate to the thread's page (soft-nav with fallback).
        // navigateTo() re-validates the URL is http(s) before using it.
        const url = "url" in thread.target ? thread.target.url : undefined;
        const safeUrl = toHttpUrl(url);
        if (!safeUrl) return;
        closeCommentsPanel();
        navigateTo(safeUrl);
      });

      nextUi.commentsList.appendChild(item);
    });
  }

  function openCommentsPanel(): void {
    if (!commentsVisible) {
      setCommentsVisible(true);
    }
    commentsOpen = true;
    closePopup();
    syncToolbarVisibility();
    if (commentsScope === "all") {
      fetchAllThreads();
    } else {
      refreshCommentsPanel();
    }
    renderPresenceBar();
  }

  function hideNamePrompt(): void {
    if (!ui) return;
    promptingForName = false;
    nameDialogMode = null;
    nameAvatarPopoverOpen = false;
    closeDialog(ui.namePrompt);
    ui.nameAvatarPopover.hidden = true;
    syncToolbarVisibility();
  }

  function setNameAvatarPopoverOpen(open: boolean): void {
    nameAvatarPopoverOpen = open;
    if (!ui) return;
    ui.nameAvatarPopover.hidden = !open;
    ui.nameAvatarButton.dataset.expanded = open ? "true" : "false";
  }

  function syncNameDialog(): void {
    if (!ui) return;
    const nextUi = ui;
    nextUi.nameInput.value = nameDraft;
    nextUi.nameAvatarImage.src = nameDraftAvatarUrl;
    nextUi.nameAvatarPopover.hidden = !nameAvatarPopoverOpen;
    nextUi.nameAvatarButton.dataset.expanded = nameAvatarPopoverOpen
      ? "true"
      : "false";

    nextUi.nameAvatarPopover
      .querySelectorAll<HTMLButtonElement>("[data-avatar-url]")
      .forEach((button) => {
        button.dataset.selected =
          button.dataset.avatarUrl === nameDraftAvatarUrl ? "true" : "false";
      });

    if (nameDialogMode === "edit") {
      nextUi.nameSkipButton.hidden = false;
      nextUi.nameSaveButton.textContent = "Save";
      nextUi.nameSaveButton.disabled = nameDraft.trim().length === 0;
      return;
    }

    nextUi.nameSkipButton.hidden = true;
    nextUi.nameSaveButton.disabled = false;
    const trimmed = nameDraft.trim();
    nextUi.nameSaveButton.textContent =
      trimmed.length === 0
        ? "Continue"
        : trimmed === currentUser.name
          ? "Join as guest"
          : "Join";
  }

  function showNamePrompt(mode: "onboarding" | "edit"): void {
    if (!ui) return;

    nameDialogMode = mode;
    if (mode === "edit") {
      nameDraft = currentUser.name;
      nameDraftAvatarUrl =
        currentUser.avatarUrl ?? avatarForName(currentUser.name);
    }
    nameAvatarPopoverOpen = false;
    promptingForName = true;
    pinMode = false;
    closeCommentsPanel();
    hoveredElement = null;
    ui.annotateButton.dataset.active = "false";
    ui.highlight.hidden = true;
    closeDialog(ui.popup);
    openDialog(ui.namePrompt);
    syncNameDialog();
    syncToolbarVisibility();

    window.requestAnimationFrame(() => {
      ui?.nameInput.focus();
    });
  }

  function finalizeIdentity(user: UserInfo, expandToolbar = false): void {
    currentUser = user;
    hasUserIdentity = true;
    saveUserIdentity(user);
    hideNamePrompt();
    if (provider || presenceProvider) {
      provider?.provider.setAwarenessField("user", currentUser);
      presenceProvider?.provider.setAwarenessField("user", currentUser);
      renderPresenceBar();
      renderFollowIndicator();
    } else {
      connectProvider();
    }
    if (expandToolbar) {
      setToolbarExpanded(true);
    }
  }

  // Open/close our overlays as native modal <dialog>s. Modal dialogs live in
  // the browser top layer and render every other element inert, which is the
  // only reliable way to keep a host page's focus trap (Radix, react-focus-
  // lock, MUI, …) from stealing focus back out of our composer. showModal can
  // throw if the dialog is detached or the environment lacks support, so fall
  // back to a plain open attribute.
  function openDialog(dialog: HTMLDialogElement): void {
    if (dialog.open) return;
    if (typeof dialog.showModal === "function") {
      try {
        dialog.showModal();
        return;
      } catch {
        // fall through to the non-modal fallback
      }
    }
    dialog.setAttribute("open", "");
  }

  function closeDialog(dialog: HTMLDialogElement): void {
    if (typeof dialog.close === "function") {
      if (dialog.open) dialog.close();
      return;
    }
    dialog.removeAttribute("open");
  }

  function closePopup(): void {
    popupState = null;
    editingMessageId = null;
    editingDraft = "";
    editJustStarted = false;
    confirmingDeleteId = null;
    infoPopoverOpen = false;
    if (!ui) return;
    closeDialog(ui.popup);
    ui.threadList.innerHTML = "";
    ui.textarea.value = "";
    ui.infoPopover.hidden = true;
    ui.statusBar.hidden = true;
  }

  function openPopup(nextState: Exclude<PopupState, null>): void {
    popupState = nextState;
    editingMessageId = null;
    editingDraft = "";
    editJustStarted = false;
    confirmingDeleteId = null;
    infoPopoverOpen = false;
    refreshPopup();
    // Drop focus straight into the composer so the user can type immediately;
    // the modal dialog keeps a host focus trap from pulling it back out.
    window.requestAnimationFrame(() => {
      if (ui && popupState && !ui.replyContainer.hidden) ui.textarea.focus();
    });
  }

  function syncStatusPills(status: AnnotationThread["status"]): void {
    if (!ui) return;
    ui.statusOpenPill.dataset.active = status === "open" ? "true" : "false";
    ui.statusManagedPill.dataset.active =
      status === "managed" ? "true" : "false";
    ui.statusResolvedPill.dataset.active =
      status === "resolved" ? "true" : "false";
  }

  function applyMessageEdit(
    threadId: string,
    messageId: string,
    text: string,
  ): void {
    if (provider) {
      editThreadMessage(provider.doc, threadId, messageId, text);
      showStatus("Comment updated");
    } else {
      const editedAt = new Date().toISOString();
      setThreads(
        threads.map((thread) => {
          if (thread.id !== threadId) return thread;
          return {
            ...thread,
            updatedAt: editedAt,
            messages: thread.messages.map((message) =>
              message.id === messageId
                ? { ...message, text, editedAt }
                : message,
            ),
          };
        }),
      );
      showStatus("Comment updated locally");
    }
    editingMessageId = null;
    editingDraft = "";
    editJustStarted = false;
    refreshPopup();
  }

  function removeMessage(threadId: string, messageId: string): void {
    if (provider) {
      deleteThreadMessage(provider.doc, threadId, messageId);
    } else {
      setThreads(
        threads
          .map((thread) => {
            if (thread.id !== threadId) return thread;
            const messages = thread.messages.filter(
              (message) => message.id !== messageId,
            );
            if (messages.length === 0) return null;
            return {
              ...thread,
              messages,
              updatedAt: new Date().toISOString(),
            };
          })
          .filter((thread): thread is AnnotationThread => thread !== null),
      );
    }
    confirmingDeleteId = null;
    showStatus("Comment deleted");
    refreshPopup();
  }

  function renderThreadMessages(thread: AnnotationThread): void {
    if (!ui) return;
    const nextUi = ui;
    nextUi.threadList.innerHTML = "";

    thread.messages.forEach((message, index) => {
      const item = document.createElement("div");
      item.className = "magic-comment-embed-thread-item";

      const row = document.createElement("div");
      row.className = "magic-comment-embed-thread-item-row";

      const body = document.createElement("div");
      body.className = "magic-comment-embed-thread-item-body";

      const author = document.createElement("div");
      author.className = "magic-comment-embed-thread-author";

      const dot = document.createElement("span");
      dot.className = "magic-comment-embed-thread-dot";
      dot.style.backgroundColor = message.author.color;
      dot.textContent = message.author.name.charAt(0).toUpperCase();

      const avatar = createAuthorAvatar(message.author, dot);

      const label = document.createElement("span");
      label.textContent = `${message.author.name} • ${new Date(
        message.createdAt,
      ).toLocaleString()}`;
      if (message.editedAt) {
        const edited = document.createElement("span");
        edited.className = "magic-comment-embed-thread-edited";
        edited.textContent = "(edited)";
        label.appendChild(edited);
      }

      author.append(avatar, label);
      body.append(author);

      const isEditing = editingMessageId === message.id;
      const isConfirming = confirmingDeleteId === message.id;

      if (isEditing) {
        const editTextarea = document.createElement("textarea");
        editTextarea.className =
          "magic-comment-embed-textarea magic-comment-embed-thread-edit";
        editTextarea.value = editingDraft;
        editTextarea.addEventListener("input", () => {
          editingDraft = editTextarea.value;
        });

        const editActions = document.createElement("div");
        editActions.className = "magic-comment-embed-thread-edit-actions";

        const cancelEdit = document.createElement("button");
        cancelEdit.className = "magic-comment-embed-popup-button";
        cancelEdit.dataset.size = "sm";
        cancelEdit.dataset.variant = "ghost";
        cancelEdit.type = "button";
        cancelEdit.textContent = "Cancel";
        cancelEdit.addEventListener("click", () => {
          editingMessageId = null;
          editingDraft = "";
          editJustStarted = false;
          refreshPopup();
        });

        const saveEdit = document.createElement("button");
        saveEdit.className = "magic-comment-embed-popup-button";
        saveEdit.dataset.size = "sm";
        saveEdit.dataset.primary = "true";
        saveEdit.type = "button";
        saveEdit.textContent = "Save";
        saveEdit.addEventListener("click", () => {
          const trimmed = editingDraft.trim();
          if (!trimmed) return;
          applyMessageEdit(thread.id, message.id, trimmed);
        });

        editActions.append(cancelEdit, saveEdit);
        body.append(editTextarea, editActions);

        if (editJustStarted) {
          editJustStarted = false;
          window.requestAnimationFrame(() => {
            editTextarea.focus();
            const len = editTextarea.value.length;
            editTextarea.setSelectionRange(len, len);
          });
        }
      } else {
        const text = document.createElement("p");
        text.className = "magic-comment-embed-thread-text";
        text.textContent = message.text;
        body.append(text);
      }

      row.append(body);

      if (!isEditing && !isConfirming) {
        const actions = document.createElement("div");
        actions.className = "magic-comment-embed-thread-actions";

        const editBtn = document.createElement("button");
        editBtn.className = "magic-comment-embed-icon-button";
        editBtn.dataset.size = "sm";
        editBtn.type = "button";
        editBtn.setAttribute("aria-label", "Edit comment");
        editBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>`;
        editBtn.addEventListener("click", () => {
          editingMessageId = message.id;
          editingDraft = message.text;
          editJustStarted = true;
          confirmingDeleteId = null;
          refreshPopup();
        });

        const delBtn = document.createElement("button");
        delBtn.className = "magic-comment-embed-icon-button";
        delBtn.dataset.size = "sm";
        delBtn.dataset.danger = "true";
        delBtn.type = "button";
        delBtn.setAttribute("aria-label", "Delete comment");
        delBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>`;
        delBtn.addEventListener("click", () => {
          editingMessageId = null;
          confirmingDeleteId = message.id;
          refreshPopup();
        });

        actions.append(editBtn, delBtn);
        row.append(actions);
      }

      item.append(row);

      if (isConfirming) {
        const isThreadDelete = thread.messages.length === 1;
        const confirm = document.createElement("div");
        confirm.className = "magic-comment-embed-thread-confirm";

        const msg = document.createElement("span");
        msg.className = "magic-comment-embed-thread-confirm-message";
        msg.textContent = isThreadDelete
          ? "Delete this comment and the entire thread?"
          : index === 0
            ? "Delete this comment? Replies will be kept."
            : "Delete this reply?";

        const cActions = document.createElement("div");
        cActions.className = "magic-comment-embed-thread-confirm-actions";

        const cancelDel = document.createElement("button");
        cancelDel.className = "magic-comment-embed-popup-button";
        cancelDel.dataset.size = "sm";
        cancelDel.dataset.variant = "ghost";
        cancelDel.type = "button";
        cancelDel.textContent = "Cancel";
        cancelDel.addEventListener("click", () => {
          confirmingDeleteId = null;
          refreshPopup();
        });

        const confirmDel = document.createElement("button");
        confirmDel.className = "magic-comment-embed-popup-button";
        confirmDel.dataset.size = "sm";
        confirmDel.dataset.danger = "true";
        confirmDel.type = "button";
        confirmDel.textContent = "Delete";
        confirmDel.addEventListener("click", () => {
          if (isThreadDelete) {
            removeThread(thread.id);
          } else {
            removeMessage(thread.id, message.id);
          }
        });

        cActions.append(cancelDel, confirmDel);
        confirm.append(msg, cActions);
        item.append(confirm);
      }

      nextUi.threadList.appendChild(item);
    });
  }

  function refreshPopup(): void {
    if (!ui || !popupState) return;
    const nextUi = ui;

    openDialog(nextUi.popup);
    // Lower bound keeps the popup on-screen on narrow (<400px) viewports,
    // where the upper bound alone would go negative.
    nextUi.popup.style.left = `${Math.max(
      12,
      Math.min(popupState.x, window.innerWidth - 400),
    )}px`;
    nextUi.popup.style.top = `${Math.max(
      12,
      Math.min(popupState.y, window.innerHeight - 360),
    )}px`;

    const currentPopupState = popupState;
    let infoText = "";
    if (currentPopupState.kind === "draft") {
      infoText = describeThread({
        id: "draft",
        author: currentUser,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: "open",
        messages: [],
        target: currentPopupState.target,
      });
    } else {
      const thread = threads.find(
        (entry) => entry.id === currentPopupState.threadId,
      );
      if (thread) infoText = describeThread(thread);
    }
    nextUi.infoPopover.textContent = infoText;
    nextUi.infoPopover.hidden = !infoPopoverOpen;
    nextUi.infoButton.dataset.active = infoPopoverOpen ? "true" : "false";

    if (popupState.kind === "draft") {
      nextUi.popupTitle.textContent = "New comment";
      nextUi.statusBar.hidden = true;
      nextUi.threadList.innerHTML = "";
      nextUi.replyContainer.hidden = false;
      nextUi.textarea.placeholder = "Describe the change you want to make.";
      nextUi.saveButton.textContent = "Save";
      return;
    }

    const threadState = popupState;
    const thread = threads.find((entry) => entry.id === threadState.threadId);
    if (!thread) {
      closePopup();
      return;
    }

    nextUi.popupTitle.textContent = "Thread";
    nextUi.statusBar.hidden = false;
    syncStatusPills(thread.status);

    renderThreadMessages(thread);

    const blocked = editingMessageId !== null || confirmingDeleteId !== null;
    nextUi.replyContainer.hidden = blocked;
    nextUi.textarea.placeholder = "Reply to this thread";
    nextUi.saveButton.textContent = "Reply";
  }

  function renderHighlight(): void {
    if (!ui || !hoveredElement || !pinMode) {
      if (ui) ui.highlight.hidden = true;
      return;
    }

    const rect = hoveredElement.getBoundingClientRect();
    ui.highlight.hidden = false;
    ui.highlight.style.left = `${rect.left}px`;
    ui.highlight.style.top = `${rect.top}px`;
    ui.highlight.style.width = `${rect.width}px`;
    ui.highlight.style.height = `${rect.height}px`;
  }

  function renderMarkers(): void {
    if (!ui) return;
    const nextUi = ui;
    nextUi.markerLayer.innerHTML = "";

    if (!commentsVisible) {
      renderRemoteCursors();
      return;
    }

    const allDomThreads = threads.filter(
      (thread) => thread.target.kind === "dom",
    );

    const positionedThreads = allDomThreads
      .map((thread, index) => ({
        thread,
        stableIndex: index,
        position: safeThreadViewportPosition(thread),
      }))
      .filter(
        (
          entry,
        ): entry is {
          thread: AnnotationThread;
          stableIndex: number;
          position: { left: number; top: number };
        } => Boolean(entry.position),
      )
      .filter(
        (entry) => !hideDiscussedComments || entry.thread.status === "open",
      );

    const overlapGroups = new Map<string, typeof positionedThreads>();
    positionedThreads.forEach((entry) => {
      const key = `${Math.round(entry.position.left / 4) * 4}:${Math.round(entry.position.top / 4) * 4}`;
      const current = overlapGroups.get(key) ?? [];
      current.push(entry);
      overlapGroups.set(key, current);
    });

    overlapGroups.forEach((group) => {
      group.forEach((entry, groupIndex) => {
        const markerPosition = getMarkerSpreadPosition(
          entry.position,
          groupIndex,
          group.length,
        );

        const marker = document.createElement("button");
        marker.className = "magic-comment-embed-marker";
        marker.setAttribute(EMBED_MARKER_ATTRIBUTE, "");
        marker.dataset.status = entry.thread.status;
        marker.style.left = `${markerPosition.left}px`;
        marker.style.top = `${markerPosition.top}px`;
        marker.textContent = String(entry.stableIndex + 1);
        marker.title = entry.thread.messages[0]?.text ?? "Comment";
        marker.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          openPopup({
            kind: "thread",
            threadId: entry.thread.id,
            x: markerPosition.left + 36,
            y: markerPosition.top,
          });
        });

        nextUi.markerLayer.appendChild(marker);
      });
    });

    renderRemoteCursors();
  }

  function renderRemoteCursors(): void {
    if (!ui) return;
    const nextUi = ui;
    nextUi.cursorLayer.innerHTML = "";

    remotePresences.forEach((presence) => {
      const cursor = document.createElement("div");
      cursor.className = "magic-comment-embed-cursor";
      cursor.style.left = `${presence.cursorX}px`;
      cursor.style.top = `${presence.cursorY}px`;

      const pointer = document.createElement("div");
      pointer.className = "magic-comment-embed-cursor-pointer";
      pointer.style.backgroundColor = presence.user.color;

      const label = document.createElement("div");
      label.className = "magic-comment-embed-cursor-label";
      label.style.backgroundColor = presence.user.color;
      label.textContent = presence.user.name;

      cursor.append(pointer, label);
      nextUi.cursorLayer.appendChild(cursor);
    });
  }

  function currentPresencePeers(): PresencePeer[] {
    const awareness = presenceProvider?.provider.awareness;
    if (!awareness) return [];
    return dedupePeersById(
      derivePresencePeers(awareness.getStates(), awareness.clientID),
    );
  }

  function peerPathLabel(peer: PresencePeer): string {
    try {
      const url = new URL(peer.url);
      return `${url.pathname}${url.hash}`;
    } catch {
      return peer.url;
    }
  }

  function renderPresenceBar(): void {
    if (!ui) return;
    const bar = ui.presenceBar;
    // Mirror the toolbar/panel visibility: the bar only shows while the widget
    // is "open" for the reviewer.
    const visible =
      Boolean(presenceProvider) && (toolbarExpanded || commentsOpen);
    bar.hidden = !visible;
    // Safe: rebuilt from scratch and only textContent/style is set from peer data.
    bar.innerHTML = "";
    if (!visible) return;

    if (hasUserIdentity) {
      const avatar = document.createElement("button");
      avatar.type = "button";
      avatar.className =
        "magic-comment-embed-presence-avatar magic-comment-embed-presence-avatar-self";
      avatar.title = `You — ${currentUser.name}`;
      avatar.setAttribute(
        "aria-label",
        `Edit your profile (${currentUser.name})`,
      );

      const img = document.createElement("img");
      img.className = "magic-comment-embed-presence-avatar-img";
      img.alt = currentUser.name;
      img.src = currentUser.avatarUrl ?? avatarForName(currentUser.name);
      avatar.appendChild(img);
      avatar.addEventListener("click", () => showNamePrompt("edit"));
      bar.appendChild(avatar);
    }

    currentPresencePeers().forEach((peer) => {
      const samePage = peer.pageId === options.pageId;
      const avatar = document.createElement("button");
      avatar.type = "button";
      avatar.className = "magic-comment-embed-presence-avatar";
      avatar.title = `${peer.name} — ${
        samePage ? "this page" : peerPathLabel(peer)
      }`;
      // Show the person's mc-avatar (droplet). Fall back to a coloured initial
      // only if the image fails to load.
      const img = document.createElement("img");
      img.className = "magic-comment-embed-presence-avatar-img";
      img.alt = peer.name;
      img.addEventListener("error", () => {
        img.remove();
        avatar.style.backgroundColor = peer.color;
        avatar.textContent = peer.name.charAt(0).toUpperCase();
      });
      img.src = peer.avatarUrl ?? avatarForName(peer.name);
      avatar.appendChild(img);
      if (!samePage) {
        avatar.classList.add("magic-comment-embed-presence-offpage");
      }
      if (peer.id && peer.id === followId) {
        avatar.classList.add("magic-comment-embed-presence-avatar-following");
      }
      avatar.addEventListener("click", () => toggleFollow(peer));
      bar.appendChild(avatar);
    });
  }

  function renderFollowIndicator(): void {
    if (!ui) return;
    const el = ui.followIndicator;
    el.innerHTML = "";
    if (!followId) {
      el.hidden = true;
      return;
    }

    const peer = resolveFollowedPeer(currentPresencePeers(), followId);
    const label = document.createElement("span");
    label.textContent = `Following ${peer?.name ?? "teammate"}`;

    const stop = document.createElement("button");
    stop.type = "button";
    stop.className = "magic-comment-embed-follow-indicator-stop";
    stop.textContent = "Stop";
    stop.addEventListener("click", () => stopFollowing());

    el.append(label, stop);
    el.hidden = false;
  }

  function toggleFollow(peer: PresencePeer): void {
    // Following requires a stable id; peers without one cannot be tracked
    // across their reconnects, so ignore the click.
    if (!peer.id) return;

    if (followId === peer.id) {
      stopFollowing();
      return;
    }

    followId = peer.id;
    saveFollowId(peer.id);
    renderPresenceBar();
    renderFollowIndicator();
    evaluateFollow();
  }

  function stopFollowing(): void {
    followId = null;
    clearFollowId();
    disconnectFollowObserver();
    window.clearTimeout(followNavTimer);
    followNavTimer = undefined;
    followNavInFlight = false;
    renderPresenceBar();
    renderFollowIndicator();
  }

  function disconnectFollowObserver(): void {
    if (followObserver) {
      followObserver.disconnect();
      followObserver = null;
    }
  }

  /**
   * A mutation caused by the widget itself (its root being torn down/rebuilt on
   * remount, or its overlay re-rendering). These must not count as evidence
   * that a soft SPA navigation "took", otherwise the fallback never fires on a
   * static multi-page site.
   */
  function isWidgetMutation(record: MutationRecord): boolean {
    const target = record.target;
    if (
      target instanceof HTMLElement &&
      target.closest(`[${EMBED_ROOT_ATTRIBUTE}]`)
    ) {
      return true;
    }
    const touched = [...record.addedNodes, ...record.removedNodes];
    return (
      touched.length > 0 &&
      touched.every(
        (node) =>
          node instanceof HTMLElement &&
          (node.hasAttribute(EMBED_ROOT_ATTRIBUTE) ||
            node.closest(`[${EMBED_ROOT_ATTRIBUTE}]`) !== null),
      )
    );
  }

  /**
   * Decide whether/how to move to the followed peer's page. Called both when
   * follow is toggled on and whenever their presence page changes. Loop- and
   * in-flight-guarded so our own catch-up never re-triggers a navigation.
   */
  function evaluateFollow(): void {
    if (!followId) return;

    const peer = resolveFollowedPeer(currentPresencePeers(), followId);
    const decision = decideFollowAction(peer, window.location.href);
    // "wait": the followed teammate is momentarily absent while their widget
    // reconnects on a page change — keep followId and resume when they
    // re-announce (same stable id). "none": already on their page. Follow is
    // only ever ended by the user pressing Stop, never by a transient absence.
    if (decision.action === "navigate") {
      navigateTo(decision.target);
    }
  }

  /**
   * Shared navigation used both by follow-mode and by clicking an off-page
   * comment in the "All" list: soft SPA push-state first, with a full-load
   * fallback (via softNavigateTo). Re-validates the URL is http(s) and guards
   * against navigating to where we already are.
   */
  function navigateTo(target: string): void {
    if (followNavInFlight) return;
    // Never navigate to where we already are (loop guard, incl. our own
    // page updating after we caught up).
    if (target === window.location.href) return;

    let targetUrl: URL;
    try {
      targetUrl = new URL(target);
    } catch {
      return;
    }
    if (targetUrl.protocol !== "http:" && targetUrl.protocol !== "https:") {
      return;
    }

    const kind = planFollowNavigation(window.location.href, target);
    if (kind === "none") return;

    if (kind === "hash") {
      // Soft fragment change within the same document (no reload).
      window.location.hash = targetUrl.hash;
      return;
    }

    if (kind === "full") {
      followNavInFlight = true;
      window.location.assign(target);
      return;
    }

    softNavigateTo(target);
  }

  function softNavigateTo(target: string): void {
    if (typeof document === "undefined" || !document.body) {
      followNavInFlight = true;
      window.location.assign(target);
      return;
    }

    followNavInFlight = true;
    disconnectFollowObserver();

    let sawHostMutation = false;
    const titleBefore = document.title;
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (!isWidgetMutation(record)) {
          sawHostMutation = true;
          break;
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // pushState is globally patched to emit our location-change event, which
    // (synchronously) remounts the widget. Register the observer only after,
    // so that remount's unmount() cannot disconnect it.
    history.pushState({}, "", target);
    try {
      window.dispatchEvent(new PopStateEvent("popstate"));
    } catch {
      // ignore
    }
    try {
      window.dispatchEvent(new CustomEvent(LOCATION_CHANGE_EVENT));
    } catch {
      // ignore
    }

    followObserver = observer;
    followNavTimer = window.setTimeout(() => {
      followNavTimer = undefined;
      const reacted = sawHostMutation || document.title !== titleBefore;
      disconnectFollowObserver();
      if (!reacted) {
        // The host is not an SPA (or ignored the route change): do a real load.
        window.location.assign(target);
        return;
      }
      followNavInFlight = false;
    }, SOFT_NAV_TIMEOUT_MS);
  }

  function connectPresenceProvider(): void {
    if (!options.collaboration || presenceProvider) return;

    presenceProvider = createMagicCommentProvider({
      serverUrl: options.collaboration.serverUrl,
      apiKey: options.collaboration.apiKey ?? "",
      projectId: options.projectId,
      reviewId: options.reviewId,
      // Awareness-only shared room across every page of this review. We never
      // read or write its Y.Doc — only its awareness.
      roomName: `${options.projectId}:${options.reviewId ?? "default"}:presence`,
      user: currentUser,
    });

    // A fresh connection means we have settled on the current page — clear any
    // stale in-flight guard so a followed peer's next move is not blocked.
    followNavInFlight = false;

    const presenceApi = presenceProvider.provider;
    presenceApi.setAwarenessField("user", currentUser);
    presenceApi.setAwarenessField("page", {
      pageId: options.pageId,
      url: window.location.href,
      title: document.title,
    });

    const awareness = presenceApi.awareness;
    if (awareness) {
      const onChange = () => {
        renderPresenceBar();
        renderFollowIndicator();
        evaluateFollow();
      };
      awareness.on("change", onChange);
      unsubscribePresenceBar = () => awareness.off("change", onChange);
    }

    renderPresenceBar();
    renderFollowIndicator();
    // Resume following across the remount; the grace window keeps us patient
    // until the followed peer re-announces on the new connection.
    evaluateFollow();
  }

  function syncLocalThreadsToProvider(): void {
    const nextProvider = provider;
    if (!nextProvider) return;

    const localThreads = localThreadState();
    if (localThreads.length === 0) return;

    const existingIds = new Set(
      readThreads(nextProvider.doc).map((thread) => thread.id),
    );
    localThreads.forEach((thread) => {
      if (existingIds.has(thread.id)) return;
      const [root, ...replies] = thread.messages;
      const created = addThread(
        nextProvider.doc,
        thread.target,
        root?.text ?? "",
        root?.author ?? thread.author,
        {
          projectId: options.projectId,
          reviewId: options.reviewId,
          pageId: options.pageId,
        },
      );
      replies.forEach((reply) => {
        addThreadReply(nextProvider.doc, created.id, reply.text, reply.author);
      });
      if (thread.status !== "open") {
        setThreadStatus(nextProvider.doc, created.id, thread.status);
      }
    });
    // Note: the local copy is intentionally NOT cleared here — it doubles as
    // an offline backup and is overwritten with canonical state by setThreads.
  }

  function connectProvider(): void {
    if (!options.collaboration || provider) return;

    provider = createMagicCommentProvider({
      serverUrl: options.collaboration.serverUrl,
      apiKey: options.collaboration.apiKey ?? "",
      projectId: options.projectId,
      reviewId: options.reviewId,
      pageId: options.pageId,
      scope: "review",
      user: currentUser,
      onStatusChange(status) {
        providerConnected = status === "connected";
        if (status === "connected") {
          showConnectedStatus();
        }
      },
    });

    // Migrate local-only threads once the doc has synced with the server, so
    // threads already known to the server (present in the local backup with
    // the same ids) are skipped instead of duplicated.
    provider.provider.on("synced", () => {
      syncLocalThreadsToProvider();
    });

    // Prefer provider state, but don't blank locally loaded threads with the
    // provider doc's initial (pre-sync) empty state.
    const providerThreads = readThreads(provider.doc);
    if (providerThreads.length > 0 || threads.length === 0) {
      setThreads(providerThreads);
    }
    unsubscribeThreads = observeThreads(provider.doc, setThreads);
    unsubscribePresence = observePresence(provider.provider, (presences) => {
      remotePresences = presences;
      renderRemoteCursors();
    });

    connectPresenceProvider();
  }

  function saveDraft(text: string): void {
    if (!popupState || popupState.kind !== "draft") return;

    const trimmed = text.trim();
    if (!trimmed) return;

    if (provider) {
      addThread(provider.doc, popupState.target, trimmed, currentUser, {
        projectId: options.projectId,
        reviewId: options.reviewId,
        pageId: options.pageId,
      });
      showStatus(
        providerConnected ? "Comment synced" : "Comment saved — syncing…",
      );
    } else {
      const nextThread = createThreadRecord(
        popupState.target,
        trimmed,
        currentUser,
        {
          projectId: options.projectId,
          reviewId: options.reviewId,
          pageId: options.pageId,
        },
      );
      setThreads([...threads, nextThread]);
      showStatus("Comment saved locally");
    }

    closePopup();
  }

  function saveReply(text: string, threadId: string): void {
    const trimmed = text.trim();
    if (!trimmed) return;

    if (provider) {
      addThreadReply(provider.doc, threadId, trimmed, currentUser);
      showStatus(providerConnected ? "Reply synced" : "Reply saved — syncing…");
      if (ui) ui.textarea.value = "";
      return;
    }

    const nextThreads = threads.map((thread) => {
      if (thread.id !== threadId) return thread;
      const now = new Date().toISOString();
      return {
        ...thread,
        updatedAt: now,
        messages: [
          ...thread.messages,
          {
            id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
            text: trimmed,
            author: currentUser,
            createdAt: now,
          },
        ],
      };
    });

    setThreads(nextThreads);
    if (ui) ui.textarea.value = "";
    showStatus("Reply saved locally");
  }

  function updateThreadStatus(
    threadId: string,
    status: AnnotationThread["status"],
  ): void {
    const thread = threads.find((entry) => entry.id === threadId);
    if (!thread || thread.status === status) return;

    if (provider) {
      setThreadStatus(provider.doc, threadId, status);
      return;
    }

    setThreads(
      threads.map((entry) =>
        entry.id === threadId
          ? {
              ...entry,
              status,
              updatedAt: new Date().toISOString(),
            }
          : entry,
      ),
    );
  }

  function removeThread(threadId: string): void {
    if (provider) {
      deleteThread(provider.doc, threadId);
      // Threads derived from the legacy annotations map would otherwise
      // survive the delete and reappear on the next read.
      deleteAnnotation(provider.doc, threadId);
    } else {
      setThreads(threads.filter((thread) => thread.id !== threadId));
    }
    closePopup();
  }

  function clearAllThreads(): void {
    if (provider) {
      const nextProvider = provider;
      threads.forEach((thread) => {
        deleteThread(nextProvider.doc, thread.id);
        // Threads derived from the legacy annotations map would otherwise
        // survive the clear and reappear on the next read.
        deleteAnnotation(nextProvider.doc, thread.id);
      });
      showStatus("Cleared shared comments");
      return;
    }

    setThreads([]);
    clearStoredThreads(options.projectId, options.reviewId, options.pageId);
    showStatus("Cleared local comments");
  }

  function flushCursorUpdate(): void {
    cursorUpdateTimer = undefined;
    if (provider && pendingCursor) {
      updateCursorPosition(provider.provider, pendingCursor.x, pendingCursor.y);
    }
    pendingCursor = null;
  }

  function handleMouseMove(event: MouseEvent): void {
    if (provider) {
      // Throttle awareness updates to at most one every 100ms (trailing).
      pendingCursor = { x: event.clientX, y: event.clientY };
      if (cursorUpdateTimer === undefined) {
        cursorUpdateTimer = window.setTimeout(flushCursorUpdate, 100);
      }
    }

    if (!pinMode || !ui) return;

    const nextTarget = getAnnotatableElement(event.target, ui.root);
    if (nextTarget === hoveredElement) return;
    hoveredElement = nextTarget;
    renderHighlight();
  }

  function handleClick(event: MouseEvent): void {
    if (
      ui &&
      popupState &&
      event.target instanceof Node &&
      !ui.popup.contains(event.target) &&
      !(
        event.target instanceof HTMLElement &&
        event.target.hasAttribute(EMBED_MARKER_ATTRIBUTE)
      )
    ) {
      closePopup();
    }

    if (!pinMode || !ui) return;

    const target = getAnnotatableElement(event.target, ui.root);
    if (!target) return;

    event.preventDefault();
    event.stopPropagation();
    hoveredElement = target;
    renderHighlight();

    openPopup({
      kind: "draft",
      target: buildDomTarget(target, event, options.metadataProvider),
      x: event.clientX + 14,
      y: event.clientY + 14,
    });
  }

  function handleScrollOrResize(): void {
    // Coalesce scroll/resize-driven renders to at most one per frame.
    if (scrollRafHandle !== null) return;
    scrollRafHandle = window.requestAnimationFrame(() => {
      scrollRafHandle = null;
      renderHighlight();
      renderMarkers();
    });
  }

  function handleWindowBlur(): void {
    if (provider) clearCursor(provider.provider);
  }

  function submitNameDialog(): void {
    if (!ui || !nameDialogMode) return;
    const trimmed = nameDraft.trim();

    if (nameDialogMode === "onboarding" && trimmed.length === 0) {
      nameDraft = currentUser.name;
      syncNameDialog();
      ui.nameInput.focus();
      return;
    }

    if (trimmed.length === 0) {
      ui.nameInput.focus();
      return;
    }

    const nextUser = createUserFromName(trimmed, nameDraftAvatarUrl);
    if (nameDialogMode === "edit") {
      finalizeIdentity({ ...nextUser, id: currentUser.id }, false);
      showStatus("Profile updated");
      return;
    }

    finalizeIdentity(nextUser, true);
  }

  function handleRootClick(event: MouseEvent): void {
    if (!ui) return;
    const target = event.target;
    if (!(target instanceof Node)) return;

    if (
      nameAvatarPopoverOpen &&
      !ui.nameAvatarButton.contains(target) &&
      !ui.nameAvatarPopover.contains(target)
    ) {
      setNameAvatarPopoverOpen(false);
    }
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.key !== "Escape" || !promptingForName) return;
    if (nameAvatarPopoverOpen) {
      setNameAvatarPopoverOpen(false);
    }
  }

  function handleLocationChange(): void {
    if (!derivePageIdFromLocation) return;

    const nextPageId = defaultPageId();
    if (nextPageId === options.pageId) return;

    const wasPinMode = pinMode;
    unmount();
    options = normalizeOptions({ ...options, pageId: nextPageId });
    mount();

    if (wasPinMode) {
      controller.startPinMode();
    }
  }

  function mount(): void {
    if (mounted || typeof document === "undefined") return;
    if (options.ui.enabled === false) {
      console.debug(
        "[magic-comment] ui.enabled is false — mount skipped (kill switch).",
      );
      return;
    }
    mounted = true;

    logWidgetBoot();
    injectStyles();
    ui = createUI();
    const nextUi = ui;
    syncToolbarVisibility();
    syncCommentsVisibilityButton();
    syncCommentsFilterButton();
    syncCommentsScopeButtons();
    syncCorner();
    nextUi.annotateButton.dataset.active = "false";

    threads = localThreadState();
    renderMarkers();
    refreshCommentsPanel();

    nextUi.toolbarToggleButton.addEventListener("pointerdown", (event) => {
      if (event.button !== 0 && event.pointerType === "mouse") return;
      const button = nextUi.toolbarToggleButton;
      const rect = button.getBoundingClientRect();
      fabDrag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
        moved: false,
      };
      try {
        button.setPointerCapture(event.pointerId);
      } catch {
        // ignore
      }
    });
    nextUi.toolbarToggleButton.addEventListener("pointermove", (event) => {
      if (!fabDrag || event.pointerId !== fabDrag.pointerId) return;
      const dx = event.clientX - fabDrag.startX;
      const dy = event.clientY - fabDrag.startY;
      if (!fabDrag.moved && Math.hypot(dx, dy) < FAB_DRAG_THRESHOLD) return;
      if (!fabDrag.moved) {
        fabDrag.moved = true;
        if (toolbarExpanded) setToolbarExpanded(false);
        nextUi.toolbarToggleButton.dataset.dragging = "true";
      }
      const button = nextUi.toolbarToggleButton;
      const x = event.clientX - fabDrag.offsetX;
      const y = event.clientY - fabDrag.offsetY;
      button.style.left = `${x}px`;
      button.style.top = `${y}px`;
      button.style.right = "auto";
      button.style.bottom = "auto";
    });
    const finishFabPointer = (event: PointerEvent, cancelled: boolean) => {
      if (!fabDrag || event.pointerId !== fabDrag.pointerId) return;
      const wasMoved = fabDrag.moved;
      const button = nextUi.toolbarToggleButton;
      try {
        button.releasePointerCapture(event.pointerId);
      } catch {
        // ignore
      }

      if (cancelled || !wasMoved) {
        fabDrag = null;
        clearFabInlinePosition();
        if (!cancelled) {
          if (!hasUserIdentity && options.promptForName !== false) {
            showNamePrompt("onboarding");
            return;
          }
          // The FAB is the single close affordance: while the comments panel is
          // open, tapping it closes the panel (and keeps the toolbar collapsed)
          // rather than toggling the toolbar.
          if (commentsOpen) {
            closeCommentsPanel();
            setToolbarExpanded(false);
          } else {
            setToolbarExpanded(!toolbarExpanded);
          }
        }
        return;
      }

      const rect = button.getBoundingClientRect();
      fabDrag = null;
      setFabCorner(
        nearestCorner(rect.left + rect.width / 2, rect.top + rect.height / 2),
      );
    };
    nextUi.toolbarToggleButton.addEventListener("pointerup", (event) => {
      finishFabPointer(event, false);
    });
    nextUi.toolbarToggleButton.addEventListener("pointercancel", (event) => {
      finishFabPointer(event, true);
    });
    nextUi.annotateButton.addEventListener("click", () => {
      if (pinMode) {
        controller.stopPinMode();
      } else {
        controller.startPinMode();
      }
      collapseToolbar();
    });
    nextUi.commentsButton.addEventListener("click", () => {
      if (commentsOpen) {
        closeCommentsPanel();
      } else {
        openCommentsPanel();
      }
      collapseToolbar();
    });
    nextUi.commentsScopePageButton.addEventListener("click", () =>
      setCommentsScope("page"),
    );
    nextUi.commentsScopeAllButton.addEventListener("click", () =>
      setCommentsScope("all"),
    );
    // View-settings controls relocated into the comments popover — same handlers,
    // no toolbar collapse (they live in the panel, not the toolbar).
    nextUi.commentsVisibilityButton.addEventListener("click", () => {
      setCommentsVisible(!commentsVisible);
    });
    nextUi.commentsFilterButton.addEventListener("click", () => {
      setHideDiscussedComments(!hideDiscussedComments);
    });
    nextUi.clearButton.addEventListener("click", () => {
      clearAllThreads();
    });
    nextUi.saveButton.addEventListener("click", () => {
      if (!popupState) return;
      if (popupState.kind === "draft") {
        saveDraft(nextUi.textarea.value);
        return;
      }
      saveReply(nextUi.textarea.value, popupState.threadId);
    });
    nextUi.cancelButton.addEventListener("click", () => {
      closePopup();
    });
    nextUi.popupCloseButton.addEventListener("click", () => {
      closePopup();
    });
    nextUi.infoButton.addEventListener("click", () => {
      infoPopoverOpen = !infoPopoverOpen;
      refreshPopup();
    });
    const handleStatusPill = (status: AnnotationThread["status"]) => {
      if (popupState?.kind !== "thread") return;
      updateThreadStatus(popupState.threadId, status);
      refreshPopup();
    };
    nextUi.statusOpenPill.addEventListener("click", () =>
      handleStatusPill("open"),
    );
    nextUi.statusManagedPill.addEventListener("click", () =>
      handleStatusPill("managed"),
    );
    nextUi.statusResolvedPill.addEventListener("click", () =>
      handleStatusPill("resolved"),
    );
    nextUi.nameSaveButton.addEventListener("click", () => {
      submitNameDialog();
    });
    nextUi.nameSkipButton.addEventListener("click", () => {
      hideNamePrompt();
    });
    nextUi.nameCloseButton.addEventListener("click", () => {
      hideNamePrompt();
    });
    nextUi.nameInput.addEventListener("input", () => {
      nameDraft = nextUi.nameInput.value;
      syncNameDialog();
    });
    nextUi.nameAvatarButton.addEventListener("click", () => {
      setNameAvatarPopoverOpen(!nameAvatarPopoverOpen);
    });
    nextUi.nameAvatarPopover.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const button = target.closest<HTMLButtonElement>("[data-avatar-url]");
      const avatarUrl = button?.dataset.avatarUrl;
      if (!avatarUrl) return;
      nameDraftAvatarUrl = avatarUrl;
      setNameAvatarPopoverOpen(false);
      syncNameDialog();
    });
    nextUi.nameInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        nextUi.nameSaveButton.click();
      }
    });

    const stopBubbling = (event: Event): void => {
      event.stopPropagation();
    };
    nextUi.root.addEventListener("pointerdown", stopBubbling);
    nextUi.root.addEventListener("mousedown", stopBubbling);
    nextUi.root.addEventListener("touchstart", stopBubbling, { passive: true });
    nextUi.root.addEventListener("click", handleRootClick);
    nextUi.root.addEventListener("click", stopBubbling);

    ensureHistoryPatched();
    document.addEventListener("mousemove", handleMouseMove, true);
    document.addEventListener("click", handleClick, true);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("hashchange", handleLocationChange);
    window.addEventListener("popstate", handleLocationChange);
    window.addEventListener(LOCATION_CHANGE_EVENT, handleLocationChange);

    if (hasUserIdentity) {
      connectProvider();
      return;
    }

    if (options.promptForName === false) {
      finalizeIdentity(currentUser);
      return;
    }
  }

  function unmount(): void {
    if (!mounted) return;
    mounted = false;

    document.removeEventListener("mousemove", handleMouseMove, true);
    document.removeEventListener("click", handleClick, true);
    document.removeEventListener("keydown", handleKeyDown);
    window.removeEventListener("scroll", handleScrollOrResize, true);
    window.removeEventListener("resize", handleScrollOrResize);
    window.removeEventListener("blur", handleWindowBlur);
    window.removeEventListener("hashchange", handleLocationChange);
    window.removeEventListener("popstate", handleLocationChange);
    window.removeEventListener(LOCATION_CHANGE_EVENT, handleLocationChange);

    if (scrollRafHandle !== null) {
      window.cancelAnimationFrame(scrollRafHandle);
      scrollRafHandle = null;
    }
    window.clearTimeout(cursorUpdateTimer);
    cursorUpdateTimer = undefined;
    pendingCursor = null;

    window.clearTimeout(statusTimer);
    unsubscribeThreads?.();
    unsubscribePresence?.();
    unsubscribePresenceBar?.();
    unsubscribeThreads = null;
    unsubscribePresence = null;
    unsubscribePresenceBar = null;
    provider?.destroy();
    provider = null;
    presenceProvider?.destroy();
    presenceProvider = null;
    providerConnected = false;

    // Follow navigation cleanup. NOTE: followId (sessionStorage) is deliberately
    // NOT cleared here — it must survive the per-navigation remount and is only
    // cleared on an explicit unfollow.
    disconnectFollowObserver();
    window.clearTimeout(followNavTimer);
    followNavTimer = undefined;
    followNavInFlight = false;

    ui?.root.remove();
    ui = null;
    popupState = null;
    hoveredElement = null;
    commentsOpen = false;
  }

  const controller: MagicCommentController = {
    mount,
    unmount,
    startPinMode() {
      pinMode = true;
      if (ui) ui.annotateButton.dataset.active = "true";
      closeCommentsPanel();
      showStatus("Click any element to leave a comment");
      renderHighlight();
    },
    stopPinMode() {
      pinMode = false;
      hoveredElement = null;
      if (ui) {
        ui.annotateButton.dataset.active = "false";
        ui.highlight.hidden = true;
      }
    },
    updateOptions(next) {
      if ("pageId" in next) {
        derivePageIdFromLocation = next.pageId == null;
      }
      options = normalizeOptions({ ...options, ...next });
      const wasMounted = mounted;
      const wasPinMode = pinMode;
      if (wasMounted) {
        unmount();
        mount();
        if (wasPinMode) {
          controller.startPinMode();
        }
      }
    },
  };

  return controller;
}
